import { useEffect, useState } from "react";
import { Plus, Edit, Trash2, X, Save } from "lucide-react";
import api, { resolveImage } from "@/lib/api";
import { toast, Toaster } from "sonner";
import ImageUpload from "@/components/ImageUpload";

const empty = { name: "", description: "", image_url: "", sort_order: 0 };

export default function AdminCategories() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => {
    const { data } = await api.get("/admin/categories");
    setItems(data || []);
  };

  useEffect(() => { load(); }, []);

  const open = (item) => {
    setForm(item ? { ...empty, ...item } : empty);
    setEditing(item || {});
  };

  const save = async () => {
    if (!form.name) { toast.error("Ad zorunlu"); return; }
    try {
      if (editing.id) {
        await api.put(`/admin/categories/${editing.id}`, form);
        toast.success("Kategori güncellendi");
      } else {
        await api.post("/admin/categories", form);
        toast.success("Kategori eklendi");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error("Hata oluştu");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Silmek istediğinize emin misiniz?")) return;
    await api.delete(`/admin/categories/${id}`);
    toast.success("Silindi");
    load();
  };

  return (
    <div data-testid="admin-categories-page">
      <Toaster position="top-right" />
      <div className="flex items-center justify-between mb-8">
        <h1 className="heading-font text-3xl text-[#0A2540]">Kategoriler</h1>
        <button onClick={() => open(null)} className="inline-flex items-center gap-2 bg-[#0066FF] hover:bg-[#0A2540] text-white px-4 py-2.5 text-sm font-semibold transition-colors" data-testid="admin-category-add">
          <Plus size={16} /> Yeni Kategori
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((c) => (
          <div key={c.id} className="bg-white border border-slate-200 overflow-hidden" data-testid={`admin-category-card-${c.slug}`}>
            {c.image_url && <img src={resolveImage(c.image_url)} alt={c.name} className="w-full aspect-video object-cover" />}
            <div className="p-5">
              <div className="text-xs text-slate-500 mb-1">Sıra: {c.sort_order}</div>
              <h3 className="heading-font text-lg text-[#0A2540] mb-2">{c.name}</h3>
              <p className="text-sm text-slate-600 line-clamp-2 mb-4">{c.description}</p>
              <div className="flex gap-3 text-xs">
                <button onClick={() => open(c)} className="text-[#0066FF] hover:underline" data-testid={`admin-category-edit-${c.slug}`}>
                  <Edit size={12} className="inline mr-1" /> Düzenle
                </button>
                <button onClick={() => remove(c.id)} className="text-red-600 hover:underline" data-testid={`admin-category-delete-${c.slug}`}>
                  <Trash2 size={12} className="inline mr-1" /> Sil
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing !== null && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white max-w-xl w-full">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="heading-font text-xl text-[#0A2540]">{editing.id ? "Kategoriyi Düzenle" : "Yeni Kategori"}</h2>
              <button onClick={() => setEditing(null)}><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs uppercase tracking-wider text-slate-500 mb-2">Ad *</label>
                <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-4 py-3 border border-slate-200 focus:border-[#0066FF] outline-none" data-testid="category-form-name" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-slate-500 mb-2">Açıklama</label>
                <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="w-full px-4 py-3 border border-slate-200 focus:border-[#0066FF] outline-none resize-none" data-testid="category-form-description" />
              </div>
              <div>
                <ImageUpload
                  value={form.image_url}
                  onChange={(url) => setForm({ ...form, image_url: url })}
                  label="Kategori Görseli"
                  testId="category-image-upload"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-slate-500 mb-2">Sıra</label>
                <input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })} className="w-full px-4 py-3 border border-slate-200 focus:border-[#0066FF] outline-none" data-testid="category-form-sort" />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="px-5 py-2.5 border border-slate-300 hover:bg-white text-sm">İptal</button>
              <button onClick={save} className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0066FF] hover:bg-[#0A2540] text-white text-sm font-semibold" data-testid="category-form-save">
                <Save size={14} /> Kaydet
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
